import axiosInstance from '../utils/axios';

/**
 * 알림 대시보드 API 서비스
 * IA-08 알림 채널 대시보드 데이터 조회
 */
const alertDashboardService = {
  /**
   * 대시보드 개요 조회
   * @param {string} timeRange - 시간 범위 (24h, 7d, 30d)
   */
  async getOverview(timeRange = "24h") {
    try {
      const response = await axiosInstance.get("/alert-dashboard/overview", {
        params: { time_range: timeRange },
      });

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message:
          response.data.message || "대시보드 정보를 불러오는데 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "대시보드 정보를 불러오는 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 실시간 상태 조회
   */
  async getRealtime() {
    try {
      const response = await axiosInstance.get("/alert-dashboard/realtime");

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message:
          response.data.message || "실시간 상태를 불러오는데 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "실시간 상태를 불러오는 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 차트 데이터 조회
   * @param {string} timeRange - 시간 범위
   * @param {string} chartType - 차트 유형
   * @param {string} groupBy - 그룹화 기준
   */
  async getChartData(
    timeRange = "24h",
    chartType = "timeline",
    groupBy = "hour"
  ) {
    try {
      const response = await axiosInstance.get("/alert-dashboard/charts", {
        params: {
          time_range: timeRange,
          chart_type: chartType,
          group_by: groupBy,
        },
      });

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message:
          response.data.message || "차트 데이터를 불러오는데 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "차트 데이터를 불러오는 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 성능 메트릭 조회
   * @param {string} timeRange - 시간 범위
   */
  async getMetrics(timeRange = "24h") {
    try {
      const response = await axiosInstance.get("/alert-dashboard/metrics", {
        params: { time_range: timeRange },
      });

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message:
          response.data.message || "성능 메트릭을 불러오는데 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "성능 메트릭을 불러오는 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 대시보드 설정 조회
   */
  async getSettings() {
    try {
      const response = await axiosInstance.get("/alert-dashboard/settings");

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || "설정을 불러오는데 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "설정을 불러오는 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 대시보드 설정 업데이트
   * @param {object} settings - 설정 데이터
   */
  async updateSettings(settings) {
    try {
      const response = await axiosInstance.put(
        "/alert-dashboard/settings",
        settings
      );

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || "설정 저장에 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "설정 저장 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 무결성 모니터링 조회
   * @param {string} timeRange - 시간 범위
   */
  async getIntegrityMonitoring(timeRange = "24h") {
    try {
      const response = await axiosInstance.get(
        "/alert-dashboard/integrity-monitoring",
        {
          params: { time_range: timeRange },
        }
      );

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message:
          response.data.message ||
          "무결성 모니터링 정보를 불러오는데 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "무결성 모니터링 정보를 불러오는 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 채널 설정 조회
   */
  async getChannelSettings() {
    try {
      const response = await axiosInstance.get(
        "/alert-dashboard/channel-settings"
      );

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message:
          response.data.message || "채널 설정을 불러오는데 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "채널 설정을 불러오는 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 채널 설정 업데이트
   * @param {string} channel - 채널 유형 (email, slack, sms, push)
   * @param {object} settings - 설정 데이터
   */
  async updateChannelSettings(channel, settings) {
    try {
      const response = await axiosInstance.put(
        "/alert-dashboard/channel-settings",
        {
          channel,
          settings,
        }
      );

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || "채널 설정 저장에 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "채널 설정 저장 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 채널 테스트 발송
   * @param {string} channel - 채널 유형 (email, slack, sms, push)
   * @param {string} recipient - 수신자 (이메일 또는 전화번호)
   */
  async testChannel(channel, recipient = "") {
    try {
      const response = await axiosInstance.post(
        "/alert-dashboard/channel-test",
        {
          channel,
          recipient,
        }
      );

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || "채널 테스트에 실패했습니다.",
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "채널 테스트 중 오류가 발생했습니다.";
      return { success: false, message: errorMessage };
    }
  },
};

export default alertDashboardService;
